/**
 * B1–B5, B7/B8 · Exercise library: catalog + own + studio exercises with
 * search/tag/level filters, duplicate-to-edit, and a create/edit modal for
 * pattern (DSL JSON) or custom-audio exercises.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EXERCISE_LEVELS, EXERCISE_TAGS, type ExercisePattern } from '@solfo/shared';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { api, uploadFile } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import PatternEditor from '../../components/PatternEditor.tsx';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Tag,
  Textarea,
} from '../../lib/ui.tsx';

interface Exercise {
  id: string;
  name: string;
  description: string;
  kind: 'pattern' | 'custom_audio' | string;
  mode: string;
  patternJson: string | null;
  audioFileId: string | null;
  videoUrl: string | null;
  tags: string;
  level: string;
  estDurationSec: number;
  defaultTempoBpm: number;
  minTempoBpm: number | null;
  maxTempoBpm: number | null;
  transposeSemisLimit: number;
  isCatalog: boolean;
  ownerCoachId: string | null;
  studioId: string | null;
}

const DEFAULT_PATTERN = JSON.stringify(
  {
    kind: 'scale',
    steps: [
      { offset: 0, beats: 0.5 },
      { offset: 2, beats: 0.5 },
      { offset: 4, beats: 0.5 },
      { offset: 2, beats: 0.5 },
      { offset: 0, beats: 0.5 },
    ],
    defaultTempoBpm: 100,
    ladderRepeats: 5,
    ladderStep: 1,
  },
  null,
  2,
);

interface FormState {
  name: string;
  description: string;
  kind: 'pattern' | 'custom_audio';
  mode: 'standard' | 'call_response';
  tags: string[];
  level: string;
  estDurationSec: string;
  videoUrl: string;
  transposeSemisLimit: string;
  minTempoBpm: string;
  maxTempoBpm: string;
  patternText: string;
  audioFileId: string | null;
  studioId: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  kind: 'pattern',
  mode: 'standard',
  tags: [],
  level: 'beginner',
  estDurationSec: '120',
  videoUrl: '',
  transposeSemisLimit: '3',
  minTempoBpm: '',
  maxTempoBpm: '',
  patternText: DEFAULT_PATTERN,
  audioFileId: null,
  studioId: '',
};

export default function ExercisesPage() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [level, setLevel] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [patternTab, setPatternTab] = useState<'editor' | 'json'>('editor');

  // the visual editor needs a parsed pattern; invalid JSON falls back to the JSON tab
  let parsedPattern: ExercisePattern | null = null;
  try {
    parsedPattern = JSON.parse(form.patternText) as ExercisePattern;
  } catch {
    parsedPattern = null;
  }

  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (tag) params.set('tag', tag);
  if (level) params.set('level', level);
  const qs = params.toString();

  const exercises = useQuery({
    queryKey: ['exercises', { search: search.trim(), tag, level }],
    queryFn: () => api<Exercise[]>(`/api/exercises${qs ? `?${qs}` : ''}`),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['exercises'] });

  const save = useMutation({
    mutationFn: (payload: { id: string | null; body: Record<string, unknown> }) =>
      payload.id
        ? api(`/api/exercises/${payload.id}`, { method: 'PATCH', body: payload.body })
        : api('/api/exercises', { body: payload.body }),
    onSuccess: () => {
      setModalOpen(false);
      invalidate();
    },
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api(`/api/exercises/${id}/duplicate`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/exercises/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    save.reset();
    setModalOpen(true);
  }

  function openEdit(ex: Exercise) {
    setEditingId(ex.id);
    setForm({
      name: ex.name,
      description: ex.description,
      kind: ex.kind === 'custom_audio' ? 'custom_audio' : 'pattern',
      mode: ex.mode === 'call_response' ? 'call_response' : 'standard',
      tags: ex.tags.split(',').filter(Boolean),
      level: ex.level,
      estDurationSec: String(ex.estDurationSec),
      videoUrl: ex.videoUrl ?? '',
      transposeSemisLimit: String(ex.transposeSemisLimit),
      minTempoBpm: ex.minTempoBpm !== null ? String(ex.minTempoBpm) : '',
      maxTempoBpm: ex.maxTempoBpm !== null ? String(ex.maxTempoBpm) : '',
      patternText: ex.patternJson ? JSON.stringify(JSON.parse(ex.patternJson), null, 2) : DEFAULT_PATTERN,
      audioFileId: ex.audioFileId,
      studioId: ex.studioId ?? '',
    });
    setFormError(null);
    save.reset();
    setModalOpen(true);
  }

  function submit() {
    setFormError(null);
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description,
      kind: form.kind,
      mode: form.mode,
      tags: form.tags,
      level: form.level,
      estDurationSec: Number(form.estDurationSec) || 120,
      videoUrl: form.videoUrl.trim() || null,
      transposeSemisLimit: Number(form.transposeSemisLimit) || 0,
      minTempoBpm: form.minTempoBpm ? Number(form.minTempoBpm) : null,
      maxTempoBpm: form.maxTempoBpm ? Number(form.maxTempoBpm) : null,
      studioId: form.studioId || null,
    };
    if (!body.name) {
      setFormError('Name is required.');
      return;
    }
    if (form.kind === 'pattern') {
      try {
        body.pattern = JSON.parse(form.patternText);
      } catch {
        setFormError('Pattern is not valid JSON — check the syntax.');
        return;
      }
    } else {
      if (!form.audioFileId) {
        setFormError('Upload an audio file for custom-audio exercises.');
        return;
      }
      body.audioFileId = form.audioFileId;
    }
    save.mutate({ id: editingId, body });
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setFormError(null);
    try {
      const uploaded = await uploadFile(file, file.name);
      setForm((f) => ({ ...f, audioFileId: uploaded.id }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  const studios = me?.studioMembers ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Exercise library</h1>
        <Button onClick={openCreate}>+ New exercise</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-56"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select className="w-auto" value={tag} onChange={(e) => setTag(e.target.value)}>
          <option value="">All tags</option>
          {EXERCISE_TAGS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select className="w-auto" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">All levels</option>
          {EXERCISE_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </Select>
      </div>

      <ErrorText error={duplicate.error ?? archive.error} />

      {exercises.isLoading ? (
        <Spinner />
      ) : exercises.error ? (
        <ErrorText error={exercises.error} />
      ) : !exercises.data || exercises.data.length === 0 ? (
        <EmptyState title="No exercises match" hint="Try clearing the filters, or create your own exercise." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {exercises.data.map((ex) => {
            const own = !ex.isCatalog && ex.ownerCoachId === me?.id;
            return (
              <Card key={ex.id} className="flex flex-col">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <p className="min-w-0 text-sm font-semibold text-slate-900">{exLabel(ex)}</p>
                  {ex.isCatalog ? (
                    <Tag tone="brand">Catalog</Tag>
                  ) : ex.studioId ? (
                    <Tag tone="amber">Studio</Tag>
                  ) : null}
                </div>
                {exDescription(ex) && (
                  <p className="mb-2 line-clamp-2 text-xs text-slate-500">{exDescription(ex)}</p>
                )}
                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  <Tag>{ex.level}</Tag>
                  <Tag>{Math.round(ex.estDurationSec / 60)} min</Tag>
                  {ex.tags
                    .split(',')
                    .filter(Boolean)
                    .slice(0, 3)
                    .map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                </div>
                <div className="mt-auto flex flex-wrap gap-1.5">
                  <Button
                    variant="secondary"
                    className="px-2.5 py-1 text-xs"
                    onClick={() => duplicate.mutate(ex.id)}
                    disabled={duplicate.isPending}
                  >
                    Duplicate
                  </Button>
                  {own && (
                    <>
                      <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => openEdit(ex)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2.5 py-1 text-xs text-red-600"
                        onClick={() => {
                          if (window.confirm(`Archive "${exLabel(ex)}"?`)) archive.mutate(ex.id);
                        }}
                        disabled={archive.isPending}
                      >
                        Archive
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit exercise' : 'New exercise'}
      >
        <div className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kind">
              <Select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as FormState['kind'] }))}
              >
                <option value="pattern">Pattern (auto-transposed)</option>
                <option value="custom_audio">Custom audio</option>
              </Select>
            </Field>
            <Field label="Level">
              <Select value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}>
                {EXERCISE_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Tags">
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {EXERCISE_TAGS.map((t) => (
                <label key={t} className="flex cursor-pointer items-center gap-1 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={form.tags.includes(t)}
                    onChange={() =>
                      setForm((f) => ({
                        ...f,
                        tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
                      }))
                    }
                  />
                  {t}
                </label>
              ))}
            </div>
          </Field>

          {form.kind === 'pattern' ? (
            <>
              <Field
                label="Player mode"
                hint="Ear training plays each phrase first, then the student sings it back with the target hidden."
              >
                <Select
                  value={form.mode}
                  onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as FormState['mode'] }))}
                >
                  <option value="standard">Guided — sing along with the target</option>
                  <option value="call_response">Ear training — call &amp; response (echo)</option>
                </Select>
              </Field>
              <Field label="Pattern" hint="Semitone offsets from a movable root — ladder repeats move the root up each pass.">
                <div className="mb-2 flex gap-1">
                  {(['editor', 'json'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setPatternTab(tab)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                        patternTab === tab ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {tab === 'editor' ? 'Visual editor' : 'JSON'}
                    </button>
                  ))}
                </div>
                {patternTab === 'editor' && parsedPattern ? (
                  <PatternEditor
                    value={parsedPattern}
                    onChange={(p) => setForm((f) => ({ ...f, patternText: JSON.stringify(p, null, 2) }))}
                  />
                ) : (
                  <>
                    {patternTab === 'editor' && (
                      <p className="mb-1 text-xs text-amber-700">
                        The pattern JSON is invalid — fix it here to use the visual editor.
                      </p>
                    )}
                    <Textarea
                      rows={8}
                      className="font-mono text-xs"
                      value={form.patternText}
                      onChange={(e) => setForm((f) => ({ ...f, patternText: e.target.value }))}
                    />
                  </>
                )}
              </Field>
            </>
          ) : (
            <Field label="Audio file" hint="Uploaded audio is streamed to your students with authorization.">
              <input
                type="file"
                accept="audio/*"
                className="block w-full text-sm text-slate-600"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
              {form.audioFileId && !uploading && <span className="text-xs text-emerald-700">Audio uploaded ✓</span>}
            </Field>
          )}

          <Field label="Video link (optional)" hint="A demo video shown alongside the exercise (B4).">
            <Input
              type="url"
              placeholder="https://…"
              value={form.videoUrl}
              onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Est. duration (s)">
              <Input
                type="number"
                min={10}
                value={form.estDurationSec}
                onChange={(e) => setForm((f) => ({ ...f, estDurationSec: e.target.value }))}
              />
            </Field>
            <Field label="Transpose ± semis">
              <Input
                type="number"
                min={0}
                max={12}
                value={form.transposeSemisLimit}
                onChange={(e) => setForm((f) => ({ ...f, transposeSemisLimit: e.target.value }))}
              />
            </Field>
            <Field label="Min tempo">
              <Input
                type="number"
                placeholder="—"
                value={form.minTempoBpm}
                onChange={(e) => setForm((f) => ({ ...f, minTempoBpm: e.target.value }))}
              />
            </Field>
            <Field label="Max tempo">
              <Input
                type="number"
                placeholder="—"
                value={form.maxTempoBpm}
                onChange={(e) => setForm((f) => ({ ...f, maxTempoBpm: e.target.value }))}
              />
            </Field>
          </div>

          {studios.length > 0 && (
            <Field label="Share into studio library" hint="Studio teachers can assign shared exercises (B8).">
              <Select value={form.studioId} onChange={(e) => setForm((f) => ({ ...f, studioId: e.target.value }))}>
                <option value="">Just me</option>
                {studios.map((m) => (
                  <option key={m.studioId} value={m.studioId}>
                    {m.studio.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {formError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          <ErrorText error={save.error} />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending || uploading}>
              {editingId ? 'Save changes' : 'Create exercise'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
